import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MonitorSmartphone, RefreshCw, Wifi, WifiOff, Radio, Loader2, Check, X, Clock, LifeBuoy, Send, UserCog } from 'lucide-react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';

// Stato sessione -> presentazione (icona, colore, etichetta IT)
const STATUS_META = {
  pending: { icon: Loader2, spin: true, cls: 'border-amber-500/40 text-amber-600', label: 'In attesa…' },
  accepted: { icon: Check, cls: 'border-emerald-500/40 text-emerald-600', label: 'Accettata' },
  declined: { icon: X, cls: 'border-rose-500/40 text-rose-600', label: 'Rifiutata' },
  expired: { icon: Clock, cls: 'border-border text-muted-foreground', label: 'Scaduta' },
};

/** Card "Chiedi aiuto al gestore": il locale invia una richiesta al gestore assegnato. */
function ManagerHelpCard({ t }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [req, setReq] = useState(null); // ultima richiesta locale -> gestore
  const reqRef = useRef(req);
  reqRef.current = req;

  // Poll dello stato finché la richiesta è pending
  useEffect(() => {
    const id = setInterval(async () => {
      const cur = reqRef.current;
      if (!cur || cur.status !== 'pending') return;
      try {
        const { data } = await api.get(`/remote/support/${cur.id}`);
        setReq(data);
        if (data.status === 'accepted') toast.success(t('remote.mgrAccepted', { defaultValue: 'Il gestore ha accettato: sessione autorizzata' }));
        else if (data.status === 'declined') toast.error(t('remote.mgrDeclined', { defaultValue: 'Il gestore ha rifiutato la richiesta' }));
        else if (data.status === 'expired') toast.warning(t('remote.mgrExpired', { defaultValue: 'Richiesta scaduta senza risposta' }));
      } catch (e) { /* silenzioso */ }
    }, 2500);
    return () => clearInterval(id);
  }, [t]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/remote/support/request', { message: message.trim() });
      setReq(data);
      toast.info(t('remote.mgrRequested', { defaultValue: 'Richiesta inviata al gestore. In attesa di risposta…' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('remote.requestError', { defaultValue: 'Impossibile inviare la richiesta' }));
    } finally { setSending(false); }
  };

  const meta = req ? STATUS_META[req.status] : null;
  const isPending = req?.status === 'pending';

  return (
    <Card data-testid="remote-manager-help-card" className="border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <LifeBuoy className="h-4 w-4 text-primary" /> {t('remote.mgrTitle', { defaultValue: 'Chiedi aiuto al gestore' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('remote.mgrDesc', { defaultValue: 'Invia una richiesta di assistenza al tuo gestore: riceverà un avviso e potrà collegarsi a questo POS dopo la tua conferma.' })}
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('remote.mgrMsgPlaceholder', { defaultValue: 'Descrivi il problema (facoltativo)' })}
            disabled={isPending || sending}
            maxLength={200}
            data-testid="remote-manager-help-message"
          />
          <Button onClick={send} disabled={isPending || sending} className="shrink-0" data-testid="remote-manager-help-send">
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
            {isPending ? t('remote.waiting', { defaultValue: 'In attesa…' }) : t('remote.mgrSend', { defaultValue: 'Invia richiesta' })}
          </Button>
        </div>
        {req && meta && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid="remote-manager-help-status">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0"><UserCog className="h-5 w-5" /></span>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{t('remote.mgrRequestLabel', { defaultValue: 'Richiesta al gestore' })}</div>
                <div className="text-xs text-muted-foreground truncate">{req.message || t('remote.noMessage', { defaultValue: 'Nessun messaggio' })}</div>
              </div>
            </div>
            <Badge variant="outline" className={cn('gap-1 shrink-0', meta.cls)} data-testid="remote-manager-help-status-badge">
              <meta.icon className={cn('h-3.5 w-3.5', meta.spin && 'animate-spin')} />
              {meta.label}{isPending && req.expires_in ? ` (${req.expires_in}s)` : ''}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function RemoteSupportPage() {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState({});   // deviceId -> {session_id, status, expires_in}
  const [requesting, setRequesting] = useState({}); // deviceId -> bool
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/remote/devices');
      setDevices(Array.isArray(data) ? data : []);
    } catch (e) { /* silenzioso */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000); // presenza in tempo quasi reale
    return () => clearInterval(id);
  }, [load]);

  // Polling stato delle sessioni ancora "pending"
  useEffect(() => {
    const id = setInterval(async () => {
      const current = sessionsRef.current;
      const pendingIds = Object.entries(current).filter(([, s]) => s.status === 'pending');
      if (pendingIds.length === 0) return;
      for (const [deviceId, s] of pendingIds) {
        try {
          const { data } = await api.get(`/remote/session/${s.session_id}`);
          setSessions((prev) => ({ ...prev, [deviceId]: { ...prev[deviceId], ...data } }));
          if (data.status === 'accepted') toast.success(t('remote.accepted', { defaultValue: 'Terminale ' + deviceId + ': accesso ACCETTATO' }));
          else if (data.status === 'declined') toast.error(t('remote.declined', { defaultValue: 'Terminale ' + deviceId + ': accesso RIFIUTATO' }));
          else if (data.status === 'expired') toast.warning(t('remote.expired', { defaultValue: 'Terminale ' + deviceId + ': richiesta SCADUTA' }));
        } catch (e) { /* silenzioso */ }
      }
    }, 2000);
    return () => clearInterval(id);
  }, [t]);

  const requestAccess = useCallback(async (device) => {
    setRequesting((prev) => ({ ...prev, [device.id]: true }));
    try {
      const { data } = await api.post('/remote/session/request', { device_id: device.id });
      setSessions((prev) => ({ ...prev, [device.id]: data }));
      toast.info(t('remote.requested', { defaultValue: 'Richiesta inviata a ' + device.id + '. In attesa di risposta…' }));
    } catch (e) {
      const msg = e?.response?.data?.detail || t('remote.requestError', { defaultValue: 'Impossibile inviare la richiesta' });
      toast.error(msg);
    } finally {
      setRequesting((prev) => ({ ...prev, [device.id]: false }));
    }
  }, [t]);

  const onlineCount = devices.filter((d) => d.online).length;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl" data-testid="remote-support-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><MonitorSmartphone className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-2xl font-bold leading-tight">{t('remote.title', { defaultValue: 'Assistenza Remota' })}</h1>
            <p className="text-sm text-muted-foreground">{t('remote.subtitle2', { defaultValue: 'Aiuto dal gestore e terminali POS registrati' })} · <span className="text-primary font-medium">{onlineCount} online</span></p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="remote-refresh"><RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} /> {t('common.refresh', { defaultValue: 'Aggiorna' })}</Button>
      </div>

      <ManagerHelpCard t={t} />

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">{t('remote.devices', { defaultValue: 'Terminali registrati' })}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {loading && devices.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Caricamento…' })}</div>
          )}
          {!loading && devices.length === 0 && (
            <div className="py-10 text-center" data-testid="remote-empty">
              <Radio className="h-10 w-10 mx-auto text-muted-foreground/40" />
              <p className="mt-3 text-sm text-muted-foreground">{t('remote.empty', { defaultValue: 'Nessun terminale registrato. Avvia l\'agente Vexel sul POS per farlo comparire qui.' })}</p>
            </div>
          )}
          {devices.map((d) => {
            const sess = sessions[d.id];
            const meta = sess ? STATUS_META[sess.status] : null;
            const isPending = sess?.status === 'pending';
            const isRequesting = !!requesting[d.id];
            return (
              <div key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid={`remote-device-${d.id}`}>
                <div className="min-w-0 flex items-center gap-3">
                  <span className={cn('flex h-9 w-9 items-center justify-center rounded-lg shrink-0', d.online ? 'bg-emerald-500/15 text-emerald-600' : 'bg-muted text-muted-foreground')}>
                    {d.online ? <Wifi className="h-5 w-5" /> : <WifiOff className="h-5 w-5" />}
                  </span>
                  <div className="min-w-0">
                    <div className="font-mono font-semibold text-sm">{d.id}</div>
                    <div className="text-xs text-muted-foreground truncate">{d.name}{d.os ? ` · ${d.os}` : ''}{d.version ? ` · v${d.version}` : ''}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {meta && (
                    <Badge variant="outline" className={cn('gap-1', meta.cls)} data-testid={`remote-session-status-${d.id}`}>
                      <meta.icon className={cn('h-3.5 w-3.5', meta.spin && 'animate-spin')} />
                      {meta.label}{isPending && sess.expires_in ? ` (${sess.expires_in}s)` : ''}
                    </Badge>
                  )}
                  <Badge variant="outline" className={cn(d.online ? 'border-emerald-500/40 text-emerald-600' : 'border-border text-muted-foreground')} data-testid={`remote-status-${d.id}`}>
                    {d.online ? '🟢 Online' : '🔴 Offline'}
                  </Badge>
                  <Button
                    size="sm"
                    disabled={!d.online || isPending || isRequesting}
                    onClick={() => requestAccess(d)}
                    data-testid={`remote-request-${d.id}`}
                  >
                    {isRequesting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                    {isPending
                      ? t('remote.waiting', { defaultValue: 'In attesa…' })
                      : t('remote.request', { defaultValue: 'Richiedi assistenza' })}
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        {t('remote.phaseNoteB', { defaultValue: 'Dopo l\'Accetta il gestore vede lo schermo di questo PC e può controllare mouse e tastiera. Richiede il modulo remoto in esecuzione (cartella "remote" dell\'agente → avvia-remoto.bat). La sessione può essere terminata in qualsiasi momento dal banner in basso.' })}
      </p>
    </div>
  );
}
